import type {
  ConceptDwellEvent,
  ConceptVisitEvent,
  FlashcardGrade,
  FlashcardGradeEvent,
  MasteryTier,
  MasteryTransitionEvent,
  MasteryTransitionReason,
  MicroQAttemptEvent,
  SessionEndEvent,
  SessionStartEvent,
  SignalEvent,
  TopicPreferenceEvent,
  WorkedExampleStepRevealedEvent,
} from '@disha/contracts';
import { newRecallState, scheduleRecall } from '@disha/selection';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useProfile } from './profile';
import { useProgress } from './progress';
import { useRetrieval } from './retrieval';
import { useSession } from './session';

/**
 * Signal store. Append-only event log + emitter that fans out events to the
 * appropriate domain stores via a reducer. Per
 * product/design/student-personalization.md Part 3.
 *
 * Design notes:
 * - Events are the *source of truth* for derived profile state. progress-store
 *   tier + visit count are projections of the signal log.
 * - We keep a capped in-memory log (last N events) for replay + telemetry.
 *   Persisted to localStorage so a refresh doesn't lose the trailing window.
 *   Older events are dropped — the long tail belongs in api/ once sync lands
 *   (RFC-0006 / RFC-0009).
 * - `emit()` is sync, optimistic, sub-millisecond. The reducer updates
 *   progress-store immediately so the next render reads fresh state.
 *   Validation runs at the schema boundary on flush, not per-emit, to keep
 *   the tap-to-render path fast.
 *
 * What's wired today: visit, dwell, mastery-transition. Micro-Q and
 * session-start/end are defined on the contract but no surface emits them
 * yet — they land with the next build queue step.
 */

const MAX_LOG = 500;

interface SignalsState {
  events: SignalEvent[];
  emit: (draft: EventDraft) => void;
  reset: () => void;
}

// Caller-facing draft. The store fills in envelope fields (eventId,
// occurredAt, studentId, sessionId, route).
export type EventDraft =
  | Omit<ConceptVisitEvent, EnvelopeKey>
  | Omit<ConceptDwellEvent, EnvelopeKey>
  | Omit<MasteryTransitionEvent, EnvelopeKey>
  | Omit<MicroQAttemptEvent, EnvelopeKey>
  | Omit<FlashcardGradeEvent, EnvelopeKey>
  | Omit<WorkedExampleStepRevealedEvent, EnvelopeKey>
  | Omit<TopicPreferenceEvent, EnvelopeKey>
  | Omit<SessionStartEvent, EnvelopeKey>
  | Omit<SessionEndEvent, EnvelopeKey>;

type EnvelopeKey = 'eventId' | 'occurredAt' | 'studentId' | 'sessionId';

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `e-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

function stampEnvelope(draft: EventDraft): SignalEvent {
  const profile = useProfile.getState();
  const session = useSession.getState();
  return {
    ...draft,
    eventId: uuid(),
    occurredAt: new Date().toISOString(),
    studentId: profile.studentId,
    sessionId: session.sessionId,
    route: typeof window !== 'undefined' ? window.location.pathname : undefined,
  } as SignalEvent;
}

// ── Reducer — derived state updates per event ───────────────────────────
//
// Keep narrow: each handler updates exactly the stores that own the projection.
// Adding a new event kind here is the single place to wire its side-effects.

function project(event: SignalEvent): void {
  switch (event.kind) {
    case 'concept:visit': {
      const progress = useProgress.getState();
      progress.bumpVisit(event.conceptId);
      // unseen → exposed is implicit on first visit. Emit a transition event
      // so the audit trail is honest, but do it synchronously to avoid a
      // re-entrant emit() (which would re-project, etc.).
      const currentTier = progress.mastery[event.conceptId] ?? 'unseen';
      if (currentTier === 'unseen') {
        progress.setMastery(event.conceptId, 'exposed');
      }
      useSession.getState().touch(event.conceptId);
      return;
    }
    case 'concept:dwell':
      // No projection today — dwell feeds the attention signal in a later
      // build step. Logged for replay.
      return;
    case 'concept:mastery-transition': {
      useProgress.getState().setMastery(event.conceptId, event.toTier);
      return;
    }
    case 'micro-q:attempt':
      // First-try correctness will feed the belief distribution. Not yet
      // projected — logged for replay.
      return;
    case 'flashcard:grade': {
      // Two projections:
      //  1. SM-2 RecallState update (spaced-retrieval scheduler).
      //  2. Mastery-tier heuristic — a Good/Easy grade promotes exposed →
      //     practicing; an Easy on practicing → mastered. Crude proxy for
      //     "this concept's signal is improving"; full Bayesian belief
      //     update lands with build queue step 12 (graph propagation).
      const nowMs = Date.parse(event.occurredAt) || Date.now();
      const retrieval = useRetrieval.getState();
      const prev = retrieval.states[event.artifactId] ?? newRecallState(event.artifactId, nowMs);
      retrieval.upsert(scheduleRecall(prev, event.grade, nowMs));

      const progress = useProgress.getState();
      const current: MasteryTier = progress.mastery[event.conceptId] ?? 'unseen';
      const promote = (next: MasteryTier) => {
        if (current === next) return;
        progress.setMastery(event.conceptId, next);
      };
      if (event.grade >= 4 && current === 'practicing') promote('mastered');
      else if (event.grade >= 3 && (current === 'unseen' || current === 'exposed'))
        promote('practicing');
      return;
    }
    case 'worked-example:step-revealed':
      // No state projection today — per-step pacing feeds the attention
      // signal in the adaptive-path selector (build queue step 5).
      // Logged for replay.
      return;
    case 'topic:preference':
      // Logged for replay; consumed by future priority selectors that
      // multiply per-topic interest into composeSession. v0 stores the
      // signal without projecting — the priority selector can fold it in
      // when there's enough volume to be load-bearing.
      return;
    case 'session:start':
    case 'session:end':
      // Lifecycle markers — not projected to any store.
      return;
  }
}

export const useSignals = create<SignalsState>()(
  persist(
    (set) => ({
      events: [],
      emit: (draft) => {
        const event = stampEnvelope(draft);
        project(event);
        set((s) => {
          const next = s.events.length >= MAX_LOG ? s.events.slice(-MAX_LOG + 1) : s.events;
          return { events: [...next, event] };
        });
      },
      reset: () => set({ events: [] }),
    }),
    {
      name: 'disha.signals',
      // Only persist the trailing window; the API takes the long tail.
      partialize: (s) => ({ events: s.events.slice(-MAX_LOG) }),
    },
  ),
);

// ── Imperative emit helper ───────────────────────────────────────────────
//
// Callsites don't need to subscribe to the store; they just want to push an
// event. This wrapper lets them call `emit(...)` without pulling the hook.

export function emit(draft: EventDraft): void {
  useSignals.getState().emit(draft);
}

// ── Typed convenience emitters ───────────────────────────────────────────
//
// Optional, but they make callsites read as English instead of literal
// shapes. Use whichever fits the call.

export function emitVisit(conceptId: string): void {
  emit({ kind: 'concept:visit', conceptId });
}

export function emitDwell(conceptId: string, elapsedMs: number, visibleMs: number): void {
  emit({ kind: 'concept:dwell', conceptId, elapsedMs, visibleMs });
}

export function emitMasteryTransition(
  conceptId: string,
  fromTier: MasteryTier,
  toTier: MasteryTier,
  reason: MasteryTransitionReason,
): void {
  emit({ kind: 'concept:mastery-transition', conceptId, fromTier, toTier, reason });
}

export function emitFlashcardGrade(
  conceptId: string,
  artifactId: string,
  grade: FlashcardGrade,
  responseMs: number,
): void {
  emit({ kind: 'flashcard:grade', conceptId, artifactId, grade, responseMs });
}

export function emitWorkedExampleStepRevealed(
  conceptId: string,
  artifactId: string,
  stepIndex: number,
  totalSteps: number,
  msSinceShown: number,
): void {
  emit({
    kind: 'worked-example:step-revealed',
    conceptId,
    artifactId,
    stepIndex,
    totalSteps,
    msSinceShown,
  });
}

export function emitTopicPreference(
  topic: string,
  direction: 'more' | 'less',
  surface: 'spark' | 'today' | 'notes',
): void {
  emit({ kind: 'topic:preference', topic, direction, surface });
}

export function emitSessionStart(): void {
  emit({ kind: 'session:start' });
}

export function emitSessionEnd(durationMs: number, conceptsTouched: string[]): void {
  emit({ kind: 'session:end', durationMs, conceptsTouched });
}
